import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import "./process.scss";
import React from "react";
import Label from "../../../components/Label/Label";
import KeyboardArrowDownIcon from "@mui/icons-material/ArrowDropDown";
import KeyboardArrowUpIcon from "@mui/icons-material/ArrowDropUp";
import { useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useSelector } from "react-redux";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";

export default function ParameterTable({
  data,
  onView,
  tableHeaders,
  openData,
  moreOption,
  handleMenuOption,
  onUnitOpClick,
}) {
  const [openRow, setOpenRow] = useState(null);
  const { unitOfOperation, site } = useSelector(
    (state) => state.shared?.productSitesUopValues || {}
  );
  const toggleExpand = (id) => {
    setOpenRow(openRow === id ? null : id);
  };

  // Group parameters by phase
  const groupByPhaseAndTable = (parameters) => {
    return parameters?.reduce((acc, param) => {
      const phase = param.phase || "";
      const tableName = param.table_level;

      if (!acc[phase]) {
        acc[phase] = {};
      }

      if (!acc[phase][tableName]) {
        acc[phase][tableName] = [];
      }

      acc[phase][tableName].push(param);

      return acc;
    }, {});
  };

  const renderGroupedSections = (row) => {
    const parameters = row?.list_parameter || [];

    // 1️⃣ Parameters without table_level and phase
    const noTableNoPhase = parameters.filter((p) => !p.table_level && !p.phase);

    // 2️⃣ Parameters with table_level but no phase
    const tableNoPhase = parameters.filter((p) => p.table_level && !p.phase);

    // 3️⃣ Parameters with phase
    const withPhase = parameters.filter((p) => p.phase);

    // Group parameters with phase by phase -> table
    const groupedByPhaseAndTable = withPhase.reduce((acc, param) => {
      const phase = param.phase || ''; // optional phase
      const table = param.table_level || ''; // optional table under phase
      if (!acc[phase]) acc[phase] = {};
      if (!acc[phase][table]) acc[phase][table] = [];
      acc[phase][table].push(param);
      return acc;
    }, {});

    // Helper function to render a parameter row
    const renderParamRow = (param, idx, padding = "15px") => (
      <TableRow key={`param-${param.parameter_id}-${idx}`} sx={{ backgroundColor: "#F5F5F5" }}>
        <TableCell sx={{ width: "30px" }} />
        <TableCell sx={{ width: "30px" }} />
        <TableCell sx={{ fontSize: 14, paddingLeft: padding }}>{row.packing_name}</TableCell>
        <TableCell>
          <Typography
            color="primary"
            sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
            onClick={() => onView(param)}
          >
            {param.label}
          </Typography>
        </TableCell>
        <TableCell sx={{ fontSize: 14 }}>{param.unit_of_measure}</TableCell>
        <TableCell sx={{ fontSize: 14 }}>{param.parameter_class}</TableCell>
        <TableCell sx={{ fontSize: 14 }}>{param.source_system}</TableCell>
        <TableCell sx={{ fontSize: 14, textTransform: "capitalize" }}>{param.datatype}</TableCell>
        <TableCell sx={{ fontSize: 14 }}>
          {param.parameter_description ? (
            <Tooltip
              title={
                <Typography sx={{ fontSize: 13, maxWidth: 300 }}>
                  {param.parameter_description}
                </Typography>
              }
              arrow
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "#f5f5f5",   // light background
                    color: "#333",               // dark text
                    border: "1px solid #e0e0e0",
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                  },
                },
                arrow: {
                  sx: {
                    color: "#f5f5f5",
                  },
                },
              }}
            >
              <DescriptionOutlinedIcon
                sx={{
                  fontSize: 20,
                  color: "#9e9e9e",
                  cursor: "pointer",
                  "&:hover": { color: "#1976d2" },
                }}
              />
            </Tooltip>
          ) : (
            "-"
          )}
        </TableCell>
        <TableCell sx={{ fontSize: 14 }}>
          <Chip
            label={param.status}
            className={`status-chip ${param.status === "active" ? "status-chip-color-green" : "status-chip-color-gray"
              }`}
          />
        </TableCell>
      </TableRow>
    );

    return (
      <React.Fragment>
        {/* 1️⃣ Parameters without table_level & phase */}
        {noTableNoPhase.map((param, idx) => renderParamRow(param, idx))}

        {/* 2️⃣ Tables directly under Unit Operation */}
        {Object.entries(
          tableNoPhase.reduce((acc, param) => {
            if (!acc[param.table_level]) acc[param.table_level] = [];
            acc[param.table_level].push(param);
            return acc;
          }, {})
        ).map(([tableName, params]) => (
          <React.Fragment key={`table-${tableName}`}>
            {/* Table header */}
            {tableName && (<TableRow sx={{ backgroundColor: "#f0f2f5", height: "12px !important" }}>
              <TableCell />
              <TableCell colSpan={9} sx={{ fontWeight: "bold", fontSize: 14, paddingLeft: "25px" }}>
                Table - {tableName}
              </TableCell>
            </TableRow>)}

            {/* Table parameters */}
            {params.map((param, idx) => renderParamRow(param, idx, "15px"))}
          </React.Fragment>
        ))}

        {/* 3️⃣ Phases */}
        {Object.entries(groupedByPhaseAndTable).map(([phase, tables]) => (
          <React.Fragment key={`phase-${phase}`}>
            {/* Phase header */}
            <TableRow sx={{ backgroundColor: "#e6ebf2", height: "16px !important" }}>
              <TableCell />
              <TableCell colSpan={9} sx={{ fontWeight: "bold", fontSize: 14, paddingLeft: "25px" }}>
                Phase - {phase}
              </TableCell>
            </TableRow>

            {/* Tables under this phase */}
            {Object.entries(tables).map(([tableName, params]) => (
              <React.Fragment key={`phase-${phase}-table-${tableName}`}>{console.log(tableName, "params")}
                {tableName && (
                  <TableRow sx={{ backgroundColor: "#f0f2f5", height: "12px !important" }}>
                    <TableCell />
                    <TableCell colSpan={9} sx={{ fontWeight: "bold", fontSize: 14, paddingLeft: "40px" }}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <SubdirectoryArrowRightIcon sx={{ mr: 1 }} />
                        Table - {tableName}
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* Parameters inside table under phase */}
                {params.map((param, idx) => renderParamRow(param, idx, "15px"))}
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  };

  return (
    <Box width="100%">
      <TableContainer
        component={Paper}
        overflow="auto"
        className="process-table-container"
        sx={{ scrollbarGutter: "stable" }}
      >
        <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead position="sticky" top={0} className="table-header">
            <TableRow>
              <TableCell className="table-header-cell" sx={{ width: "70px" }}  >Sr No.</TableCell>
              <TableCell className="table-header-cell" sx={{ width: "60px" }} />
              <TableCell className="table-header-cell">
                Unit Operation
              </TableCell>
              <TableCell className="table-header-cell">
                Parameter Name
              </TableCell>
              <TableCell className="table-header-cell">
                Unit of Measure
              </TableCell>
              <TableCell className="table-header-cell">Class</TableCell>
              <TableCell className="table-header-cell">Source</TableCell>
              <TableCell className="table-header-cell">Data Type</TableCell>
              <TableCell className="table-header-cell">Description</TableCell>
              <TableCell className="table-header-cell" sx={{ paddingLeft: '40px !important' }}>Status</TableCell>
            </TableRow>
          </TableHead>

          <TableBody
            sx={{
              "& .MuiTableRow-root": { height: 48 },
              "& .MuiTableCell-root": { py: 0.5 },
            }}
          >
            {data?.data?.results?.map((row, index) => (
              <React.Fragment key={row.packing_name}>
                {/* Parent row */}

                <TableRow>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell sx={{ width: "30px" }}>
                    <IconButton
                      size="small"
                      onClick={() => toggleExpand(row?.packing_name)}
                    >
                      {openRow === row?.packing_name ? (
                        <KeyboardArrowUpIcon sx={{ fontSize: 32 }} />
                      ) : (
                        <KeyboardArrowDownIcon sx={{ fontSize: 32 }} />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ fontSize: 14 }}>
                    {/* <Typography
                      color="primary"
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                      onClick={() => onUnitOpClick(row)}
                    >
                      {row.packing_name}
                    </Typography> */}
                    {row.packing_name}
                  </TableCell>
                  <TableCell sx={{ fontSize: 14 }}></TableCell>
                  <TableCell sx={{ fontSize: 14 }}></TableCell>
                  <TableCell sx={{ fontSize: 14 }}></TableCell>
                  <TableCell sx={{ fontSize: 14 }}></TableCell>
                  <TableCell sx={{ fontSize: 14 }}></TableCell>
                  <TableCell sx={{ fontSize: 14 }}></TableCell>
                  <TableCell sx={{ fontSize: 14, paddingLeft: "40px" }}>

                  </TableCell>
                </TableRow>

                {/* Expanded grouped section */}
                {openRow === row?.packing_name && renderGroupedSections(row)}
              </React.Fragment>
            ))}
            {(!data?.loading && (data?.data?.results?.length === 0 || data?.data === null)) && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  {site?.id ? (
                    <Label color="textSecondary" text={"No data found"} />
                  ) : (
                    <Label color="textSecondary" text={"Select Site to view Parameters"} />
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
